import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoreBar } from '@/components/ui/ScoreBar';
import { FieldStatus } from './FieldStatus';
import type { ProductDetailGtin } from '../completeness.api';

interface GtinDetailProps {
  gtins: ProductDetailGtin[];
}

export function GtinDetail({ gtins }: GtinDetailProps) {
  const { t } = useTranslation();
  const [expandedGtins, setExpandedGtins] = useState<Set<string>>(new Set());

  if (!gtins.length) return null;

  const toggle = (gtin: string) => {
    setExpandedGtins((prev) => {
      const next = new Set(prev);
      if (next.has(gtin)) next.delete(gtin);
      else next.add(gtin);
      return next;
    });
  };

  return (
    <div data-testid="gtin-detail" className="space-y-3">
      {gtins.map((gtin) => {
        const isExpanded = expandedGtins.has(gtin.gtin);
        const rounded = Math.round(gtin.overallScore);

        return (
          <div
            key={gtin.gtin}
            data-testid={`gtin-${gtin.gtin}`}
            className="rounded-xl overflow-hidden"
            style={{
              backgroundColor: 'var(--background)',
              border: '1px solid var(--border-soft)',
              boxShadow: 'var(--card-shadow)',
            }}
          >
            {/* Layer header — clickable */}
            <button
              type="button"
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
              onClick={() => toggle(gtin.gtin)}
              aria-expanded={isExpanded}
            >
              <span
                className="material-symbols-outlined shrink-0"
                style={{ fontSize: '20px', color: 'var(--primary)' }}
                aria-hidden="true"
              >
                inventory_2
              </span>

              <div className="flex-1 min-w-0">
                <span
                  className="text-[0.8125rem] font-semibold block"
                  style={{ color: 'var(--foreground-heading)' }}
                >
                  {gtin.packagingLayer}
                </span>
                <span
                  className="text-xs"
                  style={{ color: 'var(--foreground-muted)' }}
                >
                  {gtin.gtin}
                </span>
              </div>

              <ScoreBar score={gtin.overallScore} variant="compact" className="w-32" />

              <span
                className="material-symbols-outlined shrink-0 transition-transform duration-200"
                style={{
                  fontSize: '20px',
                  color: 'var(--foreground-muted)',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                aria-hidden="true"
              >
                expand_more
              </span>
            </button>

            {/* Expanded content: category scores + missing fields */}
            {isExpanded && (
              <div
                className="px-4 pb-4"
                style={{ borderTop: '1px solid var(--border-soft)' }}
              >
                {/* Category scores grid */}
                {gtin.categoryScores && Object.keys(gtin.categoryScores).length > 0 && (
                  <div className="mt-3 grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                    {Object.entries(gtin.categoryScores).map(([catId, catData]) => (
                      <div
                        key={catId}
                        className="rounded-lg px-3 py-2"
                        style={{
                          backgroundColor: 'var(--background-muted)',
                          border: '1px solid var(--border-soft)',
                          opacity: catData.applicable === false ? 0.5 : 1,
                        }}
                      >
                        <span
                          className="block text-[0.6875rem] font-medium truncate"
                          style={{ color: 'var(--foreground-muted)' }}
                        >
                          {catData.categoryName}
                        </span>
                        {catData.applicable === false ? (
                          <span className="mt-1 block text-[0.625rem]" style={{ color: '#94A3B8' }}>
                            {t('categories.notApplicable')}
                          </span>
                        ) : (
                          <ScoreBar score={catData.score} variant="mini" className="mt-1" />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Missing fields */}
                {gtin.missingFields.length === 0 ? (
                  <div className="flex items-center gap-1.5 text-xs mt-3" style={{ color: 'var(--success-dark)' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }} aria-hidden="true">
                      check_circle
                    </span>
                    {t('products.detail.fieldComplete')}
                  </div>
                ) : (
                  <div className="mt-3">
                    <span
                      className="block text-[0.6875rem] font-semibold uppercase tracking-wider mb-1.5"
                      style={{ color: 'var(--foreground-muted)' }}
                    >
                      {t('products.detail.missing')} ({gtin.missingFields.length})
                    </span>
                    <div className="divide-y" style={{ borderColor: 'var(--border-soft)' }}>
                      {gtin.missingFields.map((field) => (
                        <FieldStatus
                          key={`${gtin.gtin}-${field.fieldName}`}
                          fieldName={field.fieldName}
                          isComplete={false}
                          packagingLayer={field.category}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
